import React, { useState, useMemo } from 'react';
import { countries as countriesJSON } from 'platform-data';
import { VQ } from '../../../../../store/student/types';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';

import { IDate, IDeportation } from '../../../../../types';

interface IProps {
  index: number;

  country: string;
  removalDate: string;
  airport: string;
  reason: string;

  onSaveAndClose: (deportation: IDeportation) => void;
}

const Deportations = (props: IProps) => {
  const { index, onSaveAndClose } = props;
  const [country, setCountry] = useState(props.country);
  const [dDeportation, setDDeportation] = useState(() => getDateFromString(props.removalDate));
  const [dDeportationError, setDDeportationError] = useState('');
  const [airport, setAirport] = useState(props.airport);
  const [reason, setReason] = useState(props.reason);

  const countries = useMemo(() => [...new Set(countriesJSON.map(country => country.countryName))], []);
  const [countrySuggestions, setCountrySuggestions] = useState(countries);

  const saveAndClose = () => {
    onSaveAndClose({
      country,
      removalDate: getStringFromDate(dDeportation),
      airport,
      reason,
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onBlurCountry = (event: React.FormEvent<HTMLInputElement>) => {
    const value = (event.currentTarget.value || '').trim();
    const country = countries.includes(value) ? value : '';

    setCountry(country);
    setCountrySuggestions(countries);
  };

  const onCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countries.filter((country: string) => country.toLowerCase().includes(value.toLowerCase()));

    setCountry(value);
    setCountrySuggestions(suggestions);
  };

  const complete =
    country !== '' &&
    dDeportation.DD !== null &&
    dDeportation.MM !== null &&
    dDeportation.YYYY !== null &&
    dDeportationError === '' &&
    airport !== '' &&
    reason !== '';

  return (
    <>
      <AutocompleteInputWithConfirm
        key={`${VQ.DEPORTED_COUNTRY}-${index}`}
        id={`${VQ.DEPORTED_COUNTRY}-${index}`}
        label={`Country`}
        onChange={(evt: any) => onCountryChange(evt)}
        value={country}
        suggestions={countrySuggestions}
        onBlur={(evt: any) => onBlurCountry(evt)}
      />
      <DateInputWithConfirm
        key={`${VQ.DEPORTED_DATE}-${index}`}
        id={`${VQ.DEPORTED_DATE}-${index}`}
        label={`Date of deportation`}
        value={dDeportation}
        errorText={dDeportationError}
        onChangeDD={(evt: any) => setDDeportation({ ...dDeportation, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDDeportation({ ...dDeportation, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDDeportation({ ...dDeportation, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDDeportationError(validateDate(dDeportation))}
      />

      <InputWithConfirm
        key={`${VQ.DEPORTED_AIRPORT}-${index}`}
        id={`${VQ.DEPORTED_AIRPORT}-${index}`}
        value={airport}
        label={`Port or airport`}
        onChange={(evt: any) => {
          setAirport(evt.currentTarget.value);
        }}
      />

      <InputWithConfirm
        key={`${VQ.DEPORTED_REASON}-${index}`}
        id={`${VQ.DEPORTED_REASON}-${index}`}
        value={reason}
        label={`Reason of deportation`}
        onChange={(evt: any) => {
          setReason(evt.currentTarget.value);
        }}
      />

      <ContinueButton index={index} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} complete={complete} />
    </>
  );
};

export default Deportations;
